import { DateRangeType, Table } from '@cokitchen/cokitchen-components';
import React, { useEffect, useRef, useState } from 'react';
import PaymentTableRow from './table-row';
import {
  formatGeneralStatementCellValue,
  getFirstDayOfMonth,
  getFirstDayOfYear,
  getLastDayOfMonth,
  handleApiError
} from 'utils/functions';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import moment from 'moment';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import StatementSummary from './statement-summary';
import { MonthlyStatementType, StatementType } from 'models/statements.model';
import { TransactionSummaryType } from 'models/transactions.model';

const tableHeaders = ['Month', 'Total', 'Balance ', ''];

type Props = {
  filter: string;
};

const GeneralTable = ({ filter }: Props) => {
  const [data, setData] = useState<MonthlyStatementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfSummary, setPdfSummary] = useState<{
    dateRange: DateRangeType['date'];
    summary: TransactionSummaryType;
  } | null>(null);

  const pdfSummaryRef = useRef<HTMLDivElement | null>(null);

  const { activeCompany } = useDashboardContext();
  const companyId = activeCompany?.id;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`admin/monthly-statement`, {
        params: {
          company_id: companyId,
          start_date: getFirstDayOfYear(filter)
        }
      });
      setData(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async (month: string) => {
    try {
      const startDate = moment(`${filter} ${month}`, 'YYYY MMMM');
      const params = {
        company_id: activeCompany.id,
        start_date: startDate.format(),
        end_date: startDate.endOf('month').format()
      };
      const res = await apiService.get('admin/monthly-transaction-summary', { params });
      const summary = res.data?.data?.[0];
      setPdfSummary?.({
        dateRange: { startDate: new Date(params.start_date), endDate: new Date(params.end_date) },
        summary
      });
    } catch (error) {
      console.log(error);
    }
  };

  const createAndDownloadExcel = (month: string, data: StatementType[]) => {
    const filename = `${month}${filter}Statement.xlsx`;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, filename);
  };

  const generatePDF = async (statementData: StatementType[], month: string) => {
    const doc = new jsPDF({
      unit: 'px'
    });

    if (pdfSummaryRef.current) {
      const canvas = await html2canvas(pdfSummaryRef.current, { scale: 10 });
      const imageData = canvas.toDataURL('image/jpeg');
      doc.addImage({ imageData, x: 0, y: 0, width: 446, height: 320, format: 'JPEG' });
    }

    const columns = [
      { header: 'Date', dataKey: 'created_at' },
      { header: 'Reference', dataKey: 'tx_ref' },
      { header: 'Amount', dataKey: 'amount' },
      { header: 'Status', dataKey: 'status' },
      { header: 'Description', dataKey: 'description' },
      { header: 'Type', dataKey: 'tx_type' },
      { header: 'Balance', dataKey: 'balance_after' }
    ];

    autoTable(doc, {
      head: [columns.map((col) => col.header)],
      body: statementData.map((row) =>
        columns.map((col) => formatGeneralStatementCellValue(col, row))
      ),
      styles: { fontSize: 10 },
      headStyles: { fillColor: '#191919' },
      margin: { left: 16, right: 16 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 100 },
        2: { cellWidth: 50 },
        3: { cellWidth: 50 },
        4: { cellWidth: 65 },
        5: { cellWidth: 50 },
        6: { cellWidth: 50 }
      },
      startY: 320
    });

    doc.save(`${month}${filter}Statement.pdf`);
  };

  const handleDownload = async (month: string, format: string) => {
    try {
      await fetchSummary?.(month);
      const res = await apiService.get(`admin/statement`, {
        params: {
          company_id: companyId,
          start_date: getFirstDayOfMonth(month, filter),
          end_date: getLastDayOfMonth(month, filter)
        }
      });
      const statementData = res?.data?.data.map(
        ({
          tx_type,
          tx_ref,
          amount,
          charges,
          status,
          balance_before,
          balance_after,
          comment,
          created_at,
          updated_at,
          description,
          source
        }: StatementType) => {
          return {
            tx_type,
            tx_ref,
            amount,
            charges,
            status,
            balance_before,
            balance_after,
            comment,
            created_at,
            updated_at,
            description,
            source
          };
        }
      );
      if (format === 'xlsx') createAndDownloadExcel(month, statementData);
      if (format === 'pdf') generatePDF(statementData, month);
    } catch (err) {
      handleApiError(err);
    } finally {
      setPdfSummary(null);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId, filter]);

  return (
    <>
      {!loading && data.length === 0 ? (
        <div className='mt-8 flex items-center'>
          <p className='mt-1'>Company has no transactions for {filter}</p>
        </div>
      ) : (
        <Table headers={tableHeaders} loading={loading}>
          {data?.map((statement: MonthlyStatementType, index: number) => (
            <PaymentTableRow
              key={index}
              statement={statement}
              handleDownload={handleDownload}
              filter={filter}
            />
          ))}
        </Table>
      )}
      {pdfSummary ? (
        <StatementSummary
          elementRef={pdfSummaryRef}
          summary={pdfSummary.summary}
          dateRange={pdfSummary.dateRange}
        />
      ) : null}
    </>
  );
};
export default GeneralTable;
